import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Play, BookOpen, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  fetchSeriesById,
  generateSeriesEpisode,
  generateSeriesRemaining,
  triggerCombineAudio,
} from '@/features/series/api';
import type { Series, SeriesEpisode } from '@/features/series/types';
import { CombinedAudioPlayer } from '@/components/series/CombinedAudioPlayer';
import { RetrofitAssetButton } from '@/components/patterns/RetrofitAssetButton';
import { listChildren } from '@/features/children/api';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { useEntitlement } from '@/hooks/useEntitlement';
import { ApiError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';
import type { WorldSlug } from '@/lib/worlds';

// #1: per-world page theme, reusing the existing single-story surface tokens
// (OutputRoute / builder routes). KP is dark navy → page text flips to white.
const WORLD_BG: Record<WorldSlug, string> = {
  aa: 'bg-world-aa',
  kp: 'bg-world-kp',
  sb: 'bg-world-sb',
};
// Extends the world bg over AppShell's bottom padding so the cream doesn't
// peek through below the content (mirrors the Story-Output routes).
const NEG_MARGIN_BG_EXTEND =
  '-mb-[calc(10rem+env(safe-area-inset-bottom,0))] pb-[calc(10rem+env(safe-area-inset-bottom,0))]';

/** Season view (/series/:id) — header + per-episode on-demand generation. */
export function SeasonRoute() {
  const S = STRINGS.series.season;
  const SB = STRINGS.series.batch;
  const SC = STRINGS.series.combined;
  const { id: seriesId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { worlds, getWorldSlugById } = useStoryWorlds();
  const { entitlement } = useEntitlement();

  const [series, setSeries] = useState<Series | null>(null);
  const [childName, setChildName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generatingNum, setGeneratingNum] = useState<number | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [startingBatch, setStartingBatch] = useState(false);
  const [combining, setCombining] = useState(false);
  const [combineError, setCombineError] = useState<string | null>(null);

  // FIX A — Track episode storyIds whose audio was just retrofitted. Extends
  // the polling gate below because a text-only series has wantsAudio=false,
  // so audioStillSettling stays false and polling wouldn't start otherwise.
  // Cleared as each episode's hasAudio flips to true (server-confirmed).
  const [retrofitAudioPending, setRetrofitAudioPending] = useState<Set<string>>(new Set());

  // #1: land at the very TOP of the season overview on entry (post-confirm
  // navigation otherwise inherits the builder's scroll position).
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    rootRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  useEffect(() => {
    if (!seriesId) return;
    let active = true;
    fetchSeriesById(seriesId)
      .then((data) => {
        if (!active) return;
        setSeries(data);
        if (data.childId) {
          listChildren()
            .then((children) => {
              if (active) setChildName(children.find((c) => c.id === data.childId)?.name ?? null);
            })
            .catch(() => {
              /* child name is best-effort */
            });
        }
      })
      .catch(() => {
        if (active) setLoadError(S.loadFailed);
      });
    return () => {
      active = false;
    };
  }, [seriesId, S.loadFailed]);

  // Live refresh (#2 + #4): keep polling while ANY background work is in flight
  // so the season view updates without a navigate-away-and-back —
  //   - a batch is running, or a single episode is being created (A1),
  //   - combined audio is being built ('pending'),
  //   - an episode's audio is still rendering (storyId set but hasAudio false) →
  //     its "anhören" appears live (#2),
  //   - all episodes are generated + audio was requested but the combine hasn't
  //     produced/started yet (none/pending) → the Gesamt-Hörbuch player appears
  //     live as soon as it's ready (#4).
  // Stops at terminal state (effect re-runs when shouldPoll flips) + on unmount.
  const audioStillSettling =
    series != null &&
    series.wantsAudio === true &&
    (series.episodes.some((e) => e.storyId && !e.hasAudio) ||
      (series.episodes.length > 0 &&
        series.episodes.every((e) => e.storyId) &&
        (series.combinedAudioStatus === 'none' || series.combinedAudioStatus === 'pending')));
  const shouldPoll =
    series?.batchStatus === 'running' ||
    series?.combinedAudioStatus === 'pending' ||
    generatingNum !== null ||
    audioStillSettling ||
    retrofitAudioPending.size > 0;
  useEffect(() => {
    if (!seriesId || !shouldPoll) return;
    const id = setInterval(() => {
      fetchSeriesById(seriesId)
        .then((data) => {
          setSeries(data);
          // Clear the single-episode spinner once its story exists.
          setGeneratingNum((num) => {
            if (num === null) return null;
            const ep = data.episodes.find((e) => e.episodeNumber === num);
            return ep?.storyId ? null : num;
          });
          // FIX A — clear retrofit-audio-pending entries once the episode
          // reports hasAudio=true (server-confirmed terminal state).
          setRetrofitAudioPending((prev) => {
            if (prev.size === 0) return prev;
            const next = new Set(prev);
            for (const storyId of prev) {
              const ep = data.episodes.find((e) => e.storyId === storyId);
              if (ep?.hasAudio) next.delete(storyId);
            }
            return next.size === prev.size ? prev : next;
          });
        })
        .catch(() => {
          /* transient — next tick retries */
        });
    }, 4000);
    return () => clearInterval(id);
  }, [seriesId, shouldPoll]);

  async function handleBatch() {
    if (!seriesId || startingBatch) return;
    setBatchError(null);
    setStartingBatch(true);
    try {
      await generateSeriesRemaining(seriesId);
      // Optimistically flip to running so the trigger hides + polling starts;
      // the next poll reconciles with the backend's authoritative state.
      setSeries((prev) => (prev ? { ...prev, batchStatus: 'running' } : prev));
    } catch (err) {
      setBatchError(err instanceof ApiError ? err.message : SB.startError);
    } finally {
      setStartingBatch(false);
    }
  }

  // A1: single-episode generation stays on the season view with inline loading.
  // No navigation, no auto-open: polling tracks the episode to completion and
  // flips its row to the done state.
  async function handleGenerate(episodeNumber: number) {
    if (!seriesId || generatingNum !== null) return;
    setGenerateError(null);
    setGeneratingNum(episodeNumber);
    try {
      const res = await generateSeriesEpisode(seriesId, episodeNumber);
      if (res.alreadyGenerated) {
        const data = await fetchSeriesById(seriesId);
        setSeries(data);
        setGeneratingNum(null);
      }
      // Otherwise keep generatingNum set; the poll effect tracks completion.
    } catch (err) {
      setGenerateError(err instanceof ApiError ? err.message : S.generateFailed);
      setGeneratingNum(null);
    }
  }

  // C6: manual (re)start of the Gesamt-Hörbuch when all episodes have audio but
  // the combine isn't ready (e.g. a prior combine failed).
  async function handleCombine() {
    if (!seriesId || combining) return;
    setCombineError(null);
    setCombining(true);
    try {
      await triggerCombineAudio(seriesId);
      setSeries((prev) => (prev ? { ...prev, combinedAudioStatus: 'pending' } : prev));
    } catch (err) {
      setCombineError(err instanceof ApiError ? err.message : SC.triggerError);
    } finally {
      setCombining(false);
    }
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-[640px] px-6 py-8">
        <p role="alert" className="text-sm text-[color:var(--color-error)]">
          {loadError}
        </p>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="mx-auto flex max-w-[640px] justify-center px-6 py-16">
        <Loader2 aria-hidden className="h-6 w-6 animate-spin text-dream-navy/50" />
      </div>
    );
  }

  const episodes = [...series.episodes].sort((a, b) => a.episodeNumber - b.episodeNumber);
  const total = series.episodeCount;
  const done = episodes.filter((e) => e.storyId).length;
  const firstUngeneratedIdx = episodes.findIndex((e) => !e.storyId);
  const credits = entitlement?.totalRemaining ?? 0;
  const remainingCount = total - done;
  const batchStatus = series.batchStatus ?? 'idle';
  const combinedStatus = series.combinedAudioStatus ?? 'none';

  // A2: the episode CURRENTLY being created. Single-episode gen → its number;
  // batch → completedCount + 1 (capped). Null when nothing is being created.
  const creatingNum =
    generatingNum !== null
      ? generatingNum
      : batchStatus === 'running'
        ? Math.min(done + 1, total)
        : null;

  // C6: offer the combine trigger when every episode is done, audio was
  // requested, and the combine isn't already in progress / ready.
  const canTriggerCombine =
    remainingCount === 0 &&
    series.wantsAudio === true &&
    (combinedStatus === 'none' || combinedStatus === 'failed');

  const rawSlug = getWorldSlugById(series.worldId);
  const worldSlug: WorldSlug = rawSlug === 'kp' || rawSlug === 'sb' ? rawSlug : 'aa';
  const isDark = worldSlug === 'kp';
  const pageText = isDark ? 'text-dream-white' : 'text-dream-navy';
  const subText = isDark ? 'text-dream-white/70' : 'text-dream-navy/70';
  const creditsText = isDark ? 'text-dream-white/60' : 'text-dream-navy/60';

  const worldName = worlds?.find((w) => w.id === series.worldId)?.name ?? '';
  const subline = childName
    ? S.subline
        .replace('{world}', worldName)
        .replace('{child}', childName)
        .replace('{done}', String(done))
        .replace('{total}', String(total))
    : S.sublineNoChild
        .replace('{world}', worldName)
        .replace('{done}', String(done))
        .replace('{total}', String(total));

  return (
    <div
      ref={rootRef}
      className={`relative min-h-screen overflow-hidden scroll-mt-24 ${NEG_MARGIN_BG_EXTEND} ${WORLD_BG[worldSlug]}`}
    >
      <div className="mx-auto flex max-w-[640px] flex-col gap-5 px-6 py-6">
        <header className="flex flex-col gap-1">
          <h1 className={`text-h3 font-semibold ${pageText}`}>{series.title ?? worldName}</h1>
          <p className={`text-sm ${subText}`}>{subline}</p>
          <p className={`text-xs ${creditsText}`}>
            {S.creditsLine.replace('{credits}', String(credits))}
          </p>
        </header>

        {/* Gesamt-Hörbuch (combined-season audio). Independent of batchStatus. */}
        {combinedStatus === 'ready' && series.combinedAudioUrl && series.combinedAudioDurationMs ? (
          <CombinedAudioPlayer
            url={series.combinedAudioUrl}
            downloadUrl={series.combinedAudioDownloadUrl ?? null}
            durationMs={series.combinedAudioDurationMs}
            title={SC.heading}
            seriesTitle={series.title ?? worldName}
            seriesId={series.id}
            episodeCount={total}
          />
        ) : combinedStatus === 'pending' ? (
          remainingCount === 0 ? (
            <div className="flex flex-col gap-2 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4">
              <Button
                type="button"
                disabled
                aria-disabled="true"
                className="h-10 w-full gap-2 bg-dream-navy text-sm font-bold text-dream-white opacity-60"
              >
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                {SC.pending}
              </Button>
            </div>
          ) : (
            <p className="rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4 text-sm text-dream-navy/70">
              {SC.pending}
            </p>
          )
        ) : canTriggerCombine ? (
          <div className="flex flex-col gap-2 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4">
            {combinedStatus === 'failed' && (
              <p className="text-xs text-[color:var(--color-error)]">{SC.failed}</p>
            )}
            <Button
              type="button"
              onClick={handleCombine}
              disabled={combining}
              className="h-10 w-full gap-2 bg-dream-navy text-sm font-bold text-dream-white hover:bg-dream-navy/90 disabled:opacity-60"
            >
              {combining ? (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              ) : (
                <Play aria-hidden className="h-4 w-4" />
              )}
              {SC.trigger}
            </Button>
            {combineError && (
              <p role="alert" className="text-xs text-[color:var(--color-error)]">
                {combineError}
              </p>
            )}
          </div>
        ) : combinedStatus === 'failed' ? (
          <p className="text-xs text-dream-navy/50">{SC.failed}</p>
        ) : null}

        {/* Inline creation indicator (A1/A2): batch OR single-episode. */}
        {creatingNum !== null && (
          <div className="flex items-center gap-2 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4 text-sm text-dream-navy/70">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            {SB.running.replace('{current}', String(creatingNum)).replace('{total}', String(total))}
          </div>
        )}

        {/* Batch generation trigger + recovery states. Hidden during single-
            episode generation (generatingNum!==null); during batch running
            the button STAYS visible in a muted, non-clickable state per the
            state-driven progress design. */}
        {remainingCount > 0 && generatingNum === null && (
          <div className="flex flex-col gap-2">
            {batchStatus === 'running' ? (
              <>
                <Button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="h-11 w-full gap-2 bg-dream-gold text-sm font-bold text-dream-navy opacity-60"
                >
                  <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                  {SB.batchRunning}
                </Button>
                <p className={`text-xs ${subText}`}>{SB.batchRunningHint}</p>
              </>
            ) : batchStatus === 'stopped_credits' ? (
              <div className="flex flex-col gap-2 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4">
                <p className="text-sm text-dream-navy/80">
                  {SB.stoppedCredits
                    .replace('{done}', String(done))
                    .replace('{total}', String(total))
                    .replace('{remaining}', String(remainingCount))}
                </p>
                <Button
                  type="button"
                  onClick={handleBatch}
                  disabled={startingBatch}
                  className="h-10 w-full bg-dream-gold text-sm font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
                >
                  {SB.stoppedCreditsRetry}
                </Button>
              </div>
            ) : batchStatus === 'failed' ? (
              <div className="flex flex-col gap-2 rounded-lg border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-4">
                <p className="text-sm text-[color:var(--color-error)]">{SB.failed}</p>
                <Button
                  type="button"
                  onClick={handleBatch}
                  disabled={startingBatch}
                  className="h-10 w-full bg-dream-gold text-sm font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
                >
                  {SB.retry}
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                onClick={handleBatch}
                disabled={startingBatch}
                className="h-11 w-full bg-dream-gold text-sm font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
              >
                {startingBatch ? (
                  <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                ) : done > 0 ? (
                  SB.triggerRemaining
                ) : (
                  SB.trigger
                )}
              </Button>
            )}
            {batchError && (
              <p role="alert" className="text-xs text-[color:var(--color-error)]">
                {batchError}
              </p>
            )}
          </div>
        )}

        {generateError && (
          <div
            role="alert"
            className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
          >
            {generateError}
          </div>
        )}

        <ul className="flex flex-col gap-3">
          {episodes.map((ep, i) => (
            <EpisodeRow
              key={ep.id}
              episode={ep}
              isNext={i === firstUngeneratedIdx}
              credits={credits}
              generating={creatingNum === ep.episodeNumber}
              seriesWantsAudio={series.wantsAudio === true}
              onOpen={() => ep.storyId && navigate(`/story/${ep.storyId}`)}
              onGenerate={() => handleGenerate(ep.episodeNumber)}
              retrofitAudioInFlight={ep.storyId ? retrofitAudioPending.has(ep.storyId) : false}
              onRetrofitAudioEnqueued={() => {
                if (!ep.storyId) return;
                setRetrofitAudioPending((prev) => {
                  const next = new Set(prev);
                  next.add(ep.storyId!);
                  return next;
                });
              }}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

interface EpisodeRowProps {
  episode: SeriesEpisode;
  isNext: boolean;
  credits: number;
  generating: boolean;
  /**
   * Series-level `wantsAudio` flag. Threaded so the row can decide
   * whether a missing audio asset should surface a "wird erstellt …"
   * spinner (audio expected, not yet ready) versus the RetrofitAssetButton
   * (audio never expected, opt-in path).
   */
  seriesWantsAudio: boolean;
  onOpen: () => void;
  onGenerate: () => void;
  /**
   * FIX A — Local flag: has retrofit-audio been enqueued for this
   * episode's storyId (SeasonRoute owns the set). Drives the pending
   * hint through RetrofitAssetButton's `currentStatus='pending'` shim.
   */
  retrofitAudioInFlight: boolean;
  /** FIX A — Fired on 202 from the retrofit-audio POST. */
  onRetrofitAudioEnqueued: () => void;
}

function EpisodeRow({
  episode,
  isNext,
  credits,
  generating,
  seriesWantsAudio,
  onOpen,
  onGenerate,
  retrofitAudioInFlight,
  onRetrofitAudioEnqueued,
}: EpisodeRowProps) {
  const S = STRINGS.series.season;
  const generated = Boolean(episode.storyId);
  const isGenerating = generating || episode.status === 'generating';
  // Paket-2 state-derived signal for the per-episode audio spinner. When the
  // series wanted audio and the text exists but audio does NOT yet — assume
  // it is currently being worked on (batch cascade, retrofit fire, or the
  // per-episode-generation's follow-on audio job). Survives page reload
  // because the signal is pure server state.
  const audioBeingWorkedOn =
    seriesWantsAudio && generated && !episode.hasAudio;

  return (
    <li className="flex items-center gap-3 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4">
      <div className="min-w-0 flex-1">
        <p
          className="line-clamp-3 text-sm font-semibold text-dream-navy"
          title={`${episode.episodeNumber}. ${episode.title}`}
        >
          {episode.episodeNumber}. {episode.title}
        </p>
        {episode.teaser?.trim() && (
          <p className="line-clamp-2 text-xs text-dream-navy/70" title={episode.teaser}>
            {episode.teaser}
          </p>
        )}
      </div>

      {generated ? (
        // A3: "Lesen" always (opens the reader); "Anhören" only when audio exists
        // (audio is opt-in). Both open the story view. Paket 2: when the series
        // wanted audio and it is not yet ready, replace the "Anhören"/retrofit
        // slot with a state-derived "wird erstellt …" spinner (see
        // audioBeingWorkedOn above). RetrofitAssetButton still surfaces when
        // audio was NOT requested at series creation (opt-in add path).
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            onClick={onOpen}
            className="h-10 gap-1.5 bg-dream-navy px-4 text-sm font-medium text-dream-white hover:bg-dream-navy/90"
          >
            <BookOpen aria-hidden className="h-4 w-4" />
            {S.episodeRead}
          </Button>
          {episode.hasAudio ? (
            <Button
              type="button"
              onClick={onOpen}
              className="h-10 gap-1.5 border-[1.5px] border-dream-navy bg-transparent px-4 text-sm font-medium text-dream-navy hover:bg-dream-navy/5"
            >
              <Play aria-hidden className="h-4 w-4" />
              {S.episodeListen}
            </Button>
          ) : audioBeingWorkedOn ? (
            <span
              className="flex shrink-0 items-center gap-1.5 text-sm text-dream-navy/70"
              aria-live="polite"
            >
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              {S.episodeGenerating}
            </span>
          ) : episode.storyId ? (
            <RetrofitAssetButton
              storyId={episode.storyId}
              variant="audio"
              // Present a shim status: 'not_requested' when idle, 'pending'
              // once retrofit has been fired. The button hides itself once
              // hasAudio flips true on the next poll (this whole branch
              // switches to "Anhören").
              currentStatus={retrofitAudioInFlight ? 'pending' : 'not_requested'}
              onEnqueued={onRetrofitAudioEnqueued}
              testId={`episode-${episode.episodeNumber}-retrofit-audio`}
            />
          ) : null}
        </div>
      ) : isGenerating ? (
        <span className="flex shrink-0 items-center gap-1.5 text-sm text-dream-navy/70">
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          {S.episodeGenerating}
        </span>
      ) : isNext ? (
        credits > 0 ? (
          <Button
            type="button"
            onClick={onGenerate}
            className="h-10 shrink-0 bg-dream-gold px-4 text-sm font-bold text-dream-navy hover:bg-dream-gold/90"
          >
            {S.episodeGenerate}
          </Button>
        ) : (
          <span className="shrink-0 text-xs text-dream-navy/60">{S.noCreditsHint}</span>
        )
      ) : (
        <span className="flex shrink-0 items-center gap-1.5 text-sm text-dream-navy/50">
          <Lock aria-hidden className="h-4 w-4" />
          {S.episodePlanned}
        </span>
      )}
    </li>
  );
}
